import { MY_ACTIVE_REGISTRATION_STATUSES } from "./studentCases";
import { supabase } from "./supabase";

export type RecentCandidateRow = {
  student_id: string;
  full_name: string | null;
  initials: string;
  university: string | null;
  major: string | null;
  team_name: string | null;
  submitted_count: number;
  /** "in_progress" | "submitted" — drives the badge tone. */
  status: "in_progress" | "submitted";
};

export type RecentSubmissionRow = {
  submission_id: string;
  file_name: string;
  team_name: string | null;
  deliverable_name: string | null;
  submitted_at: string;
  status: "submitted" | "late" | "reviewed" | string;
  size_bytes: number | null;
};

function makeInitials(name: string | null): string {
  if (!name?.trim()) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * Top candidates for the "Live Feed" panel. Returns the most recent
 * registrations for a competition with their team + submission count.
 *
 * Uses parallel separate queries instead of a PostgREST embed on
 * `students`. The embed path was rendering 0 rows for clients shortly
 * after the RLS policy that opened up `students` to client SELECT
 * went in — embeds + RLS race against the schema cache. Separate
 * queries sidestep the issue and run in parallel anyway.
 */
export async function listTopCandidates(
  competitionId: string,
  limit = 4
): Promise<RecentCandidateRow[]> {
  // 1. Pull the registration rows (no embed).
  const { data: regs, error: regsErr } = await supabase
    .from("registrations")
    .select("student_id, created_at")
    .eq("competition_id", competitionId)
    .in("status", MY_ACTIVE_REGISTRATION_STATUSES as unknown as string[])
    .order("created_at", { ascending: false })
    .limit(limit);

  if (regsErr) {
    console.warn("listTopCandidates: registrations query failed", regsErr);
    return [];
  }
  if (!regs?.length) return [];

  const studentIds = regs.map((r) => r.student_id as string);

  // 2. Parallel: students, profiles, team_members, submissions.
  const [
    { data: students },
    { data: profiles },
    { data: members },
    { data: subs },
  ] = await Promise.all([
    supabase
      .from("students")
      .select("id, university, major")
      .in("id", studentIds),
    supabase
      .from("user_profiles")
      .select("id, full_name")
      .in("id", studentIds),
    supabase
      .from("team_members")
      .select("student_id, team_id, teams ( name )")
      .eq("competition_id", competitionId)
      .in("student_id", studentIds),
    supabase
      .from("submissions")
      .select("team_id")
      .eq("competition_id", competitionId),
  ]);

  const studentById = new Map<string, { university: string | null; major: string | null }>();
  for (const s of students ?? []) {
    studentById.set(s.id as string, {
      university: (s as { university: string | null }).university,
      major: (s as { major: string | null }).major,
    });
  }
  const nameById = new Map<string, string | null>();
  for (const p of profiles ?? []) {
    nameById.set(p.id as string, (p as { full_name: string | null }).full_name);
  }
  type MemberRow = {
    student_id: string;
    team_id: string;
    teams: { name: string } | { name: string }[] | null;
  };
  const teamNameByStudent = new Map<string, string | null>();
  const teamIdByStudent = new Map<string, string>();
  for (const m of (members ?? []) as MemberRow[]) {
    const t = Array.isArray(m.teams) ? m.teams[0] : m.teams;
    teamNameByStudent.set(m.student_id, t?.name ?? null);
    teamIdByStudent.set(m.student_id, m.team_id);
  }
  const subCountByTeam = new Map<string, number>();
  for (const s of (subs ?? []) as { team_id: string }[]) {
    subCountByTeam.set(s.team_id, (subCountByTeam.get(s.team_id) ?? 0) + 1);
  }

  return regs.map((r) => {
    const sid = r.student_id as string;
    const stu = studentById.get(sid);
    const name = nameById.get(sid) ?? null;
    const teamId = teamIdByStudent.get(sid);
    const subCount = teamId ? subCountByTeam.get(teamId) ?? 0 : 0;
    return {
      student_id: sid,
      full_name: name,
      initials: makeInitials(name),
      university: stu?.university ?? null,
      major: stu?.major ?? null,
      team_name: teamNameByStudent.get(sid) ?? null,
      submitted_count: subCount,
      status: subCount > 0 ? ("submitted" as const) : ("in_progress" as const),
    };
  });
}

/**
 * Most-recent submissions for a competition.
 */
export async function listRecentSubmissions(
  competitionId: string,
  limit = 4
): Promise<RecentSubmissionRow[]> {
  const { data, error } = await supabase
    .from("submissions")
    .select(
      "id, file_name, file_size_bytes, status, submitted_at, team_id, deliverable_id, teams ( name ), deliverables ( name )"
    )
    .eq("competition_id", competitionId)
    .order("submitted_at", { ascending: false })
    .limit(limit);

  if (error || !data?.length) return [];

  type Row = {
    id: string;
    file_name: string;
    file_size_bytes: number | null;
    status: string;
    submitted_at: string;
    teams: { name: string } | { name: string }[] | null;
    deliverables: { name: string } | { name: string }[] | null;
  };

  return (data as Row[]).map((r) => {
    const t = Array.isArray(r.teams) ? r.teams[0] : r.teams;
    const d = Array.isArray(r.deliverables) ? r.deliverables[0] : r.deliverables;
    return {
      submission_id: r.id,
      file_name: r.file_name,
      team_name: t?.name ?? null,
      deliverable_name: d?.name ?? null,
      submitted_at: r.submitted_at,
      status: r.status as RecentSubmissionRow["status"],
      size_bytes: r.file_size_bytes,
    };
  });
}

export type ProgramTimelinePoint = {
  key: string;
  label: string;
  state: "done" | "current" | "upcoming";
  dateText: string;
};

/**
 * Synthesises a 4–5 point timeline for a competition from the date fields
 * already on the row. Doesn't require any extra schema.
 */
export function buildProgramTimeline(comp: {
  registration_open_at: string | null;
  opens_at: string | null;
  closes_at: string | null;
  status: string;
  judging_started_at?: string | null;
  results_announced_at?: string | null;
}): ProgramTimelinePoint[] {
  const now = Date.now();
  const fmtDate = (iso: string | null) => {
    if (!iso) return "TBC";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "TBC";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  const passed = (iso: string | null) => Boolean(iso && new Date(iso).getTime() < now);

  const opens = comp.opens_at ? new Date(comp.opens_at).getTime() : null;
  const closes = comp.closes_at ? new Date(comp.closes_at).getTime() : null;

  const stages: ProgramTimelinePoint[] = [];

  if (comp.registration_open_at) {
    stages.push({
      key: "reg",
      label: "Pre-Registration Open",
      state: passed(comp.registration_open_at) ? "done" : "current",
      dateText: fmtDate(comp.registration_open_at),
    });
  }

  if (opens != null) {
    stages.push({
      key: "brief",
      label: "Brief Released — Work Period Begins",
      state: opens < now ? "done" : "upcoming",
      dateText: fmtDate(comp.opens_at),
    });
  }

  if (closes != null) {
    let state: ProgramTimelinePoint["state"] = "upcoming";
    if (closes < now) state = "done";
    else if (opens != null && opens < now) state = "current";
    const days = Math.max(0, Math.ceil((closes - now) / 86_400_000));
    const sub =
      state === "current"
        ? `${fmtDate(comp.closes_at)} · ${days} day${days === 1 ? "" : "s"} remaining`
        : fmtDate(comp.closes_at);
    stages.push({
      key: "deadline",
      label: state === "current" ? "Submission Deadline ← Now" : "Submission Deadline",
      state,
      dateText: sub,
    });
  }

  // Judging + results — derive purely from status.
  stages.push({
    key: "judging",
    label: "Judging",
    state:
      comp.status === "judging"
        ? "current"
        : comp.status === "closed"
          ? "done"
          : "upcoming",
    dateText: comp.status === "judging" ? "In progress" : comp.status === "closed" ? "Complete" : "TBC",
  });

  stages.push({
    key: "results",
    label: "Results & Winner's Circle",
    state: comp.status === "closed" ? "done" : "upcoming",
    dateText: comp.status === "closed" ? "Announced" : "TBC",
  });

  return stages;
}

/**
 * Quick-select: "active" competition for the dashboard focal point.
 * Picks the open one with the soonest deadline; falls back to most recent
 * draft / closed when no opens.
 */
export function pickFocusCompetition<
  T extends { status: string; closes_at: string | null; created_at?: string | null },
>(rows: T[]): T | null {
  if (rows.length === 0) return null;
  const opens = rows.filter((r) => r.status === "open" || r.status === "judging");
  if (opens.length > 0) {
    const withDeadline = opens.filter((r) => r.closes_at);
    if (withDeadline.length > 0) {
      withDeadline.sort(
        (a, b) =>
          new Date(a.closes_at as string).getTime() -
          new Date(b.closes_at as string).getTime()
      );
      return withDeadline[0];
    }
    return opens[0];
  }
  const sorted = [...rows].sort((a, b) => {
    const at = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
    return bt - at;
  });
  return sorted[0];
}

// ─── All-candidates (cross-case search) ──────────────────────────────────

export type AllCandidatesCaseRef = {
  competition_id: string;
  competition_name: string;
  status: string;
  engagement_model: "licensing" | "rm" | "hybrid";
  /** Shortlist row id when the calling client has shortlisted this student
   *  in this case. Pass to `removeFromShortlist(id)` to undo. */
  shortlist_entry_id: string | null;
};

export type AllCandidatesRow = {
  student_id: string;
  full_name: string | null;
  initials: string;
  avatar_url: string | null;
  university: string | null;
  major: string | null;
  graduation_year: number | null;
  graduation_term: string | null;
  state: string | null;
  city: string | null;
  /** Cases this student has registered for. cases[0] is the most-recent
   *  registration ("primary case") — Contact popover writes there by default. */
  cases: AllCandidatesCaseRef[];
  /** True if shortlisted in any of the client's cases. */
  shortlisted: boolean;
  /** Sum of submissions across all teams this student is a member of, in any
   *  of the client's competitions. */
  submission_count: number;
  /** Latest activity timestamp — max(submitted_at) on the student's teams,
   *  falls back to most_recent_registered_at when there are no submissions. */
  last_active_at: string | null;
  /** Latest registration timestamp across all of the client's cases. */
  most_recent_registered_at: string | null;
};

import { supabase as _supabase_for_all_candidates } from "./supabase";

function makeInitialsForAll(name: string | null): string {
  if (!name?.trim()) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * Cross-case talent pool. Returns every student who has ever registered for
 * any of the calling client's competitions. RLS on the registrations table
 * already restricts visibility to the caller's company, so this query is
 * safe — we don't need to filter by client_id manually.
 *
 * Uses parallel separate queries instead of PostgREST embeds on `students`
 * and `competitions`. The embed path was returning empty rows for client
 * users due to the same RLS-vs-schema-cache race that bit `listTopCandidates`.
 * Same fix pattern: pull registrations standalone, then fan out to all
 * related tables in one Promise.all and hydrate via Maps.
 */
export async function listAllCandidatesForClient(): Promise<AllCandidatesRow[]> {
  const { data: regs, error: regsErr } = await _supabase_for_all_candidates
    .from("registrations")
    .select("student_id, created_at, competition_id, status")
    .order("created_at", { ascending: false });

  if (regsErr) {
    console.warn("listAllCandidatesForClient: registrations query failed", regsErr);
    return [];
  }
  if (!regs?.length) return [];

  type RegRow = {
    student_id: string;
    created_at: string;
    competition_id: string;
    status: string;
  };
  const regRows = regs as RegRow[];

  const studentIds = Array.from(new Set(regRows.map((r) => r.student_id)));
  const competitionIds = Array.from(new Set(regRows.map((r) => r.competition_id)));
  if (studentIds.length === 0) return [];

  // Parallel: students (academic fields formerly embedded), competitions
  // (case meta formerly embedded), profiles (name/avatar), shortlist hits,
  // team memberships.
  const [
    { data: students },
    { data: competitions },
    { data: profiles },
    { data: short },
    { data: members },
  ] = await Promise.all([
    _supabase_for_all_candidates
      .from("students")
      .select("id, university, major, graduation_year, graduation_term, state, city")
      .in("id", studentIds),
    _supabase_for_all_candidates
      .from("competitions")
      .select("id, name, status, engagement_model")
      .in("id", competitionIds),
    _supabase_for_all_candidates
      .from("user_profiles")
      .select("id, full_name, avatar_url")
      .in("id", studentIds),
    _supabase_for_all_candidates
      .from("shortlist_entries")
      .select("id, student_id, competition_id")
      .in("student_id", studentIds),
    _supabase_for_all_candidates
      .from("team_members")
      .select("student_id, team_id, competition_id")
      .in("student_id", studentIds),
  ]);

  type StudentRow = {
    id: string;
    university: string | null;
    major: string | null;
    graduation_year: number | null;
    graduation_term: string | null;
    state: string | null;
    city: string | null;
  };
  type CompRow = {
    id: string;
    name: string;
    status: string;
    engagement_model: "licensing" | "rm" | "hybrid";
  };

  const studentById = new Map<string, StudentRow>();
  for (const s of (students ?? []) as StudentRow[]) {
    studentById.set(s.id, s);
  }
  const compById = new Map<string, CompRow>();
  for (const c of (competitions ?? []) as CompRow[]) {
    compById.set(c.id, c);
  }

  // Group by student. cases[] preserves the registration-desc order from
  // the outer query, so cases[0] is the most recent.
  const byStudent = new Map<string, AllCandidatesRow>();
  for (const r of regRows) {
    const stu = studentById.get(r.student_id);
    const comp = compById.get(r.competition_id);
    const caseRef: AllCandidatesCaseRef = {
      competition_id: r.competition_id,
      competition_name: comp?.name ?? "",
      status: r.status,
      engagement_model: (comp?.engagement_model ?? "licensing") as AllCandidatesCaseRef["engagement_model"],
      shortlist_entry_id: null,
    };
    const existing = byStudent.get(r.student_id);
    if (existing) {
      existing.cases.push(caseRef);
      if (
        !existing.most_recent_registered_at ||
        new Date(r.created_at).getTime() > new Date(existing.most_recent_registered_at).getTime()
      ) {
        existing.most_recent_registered_at = r.created_at;
      }
    } else {
      byStudent.set(r.student_id, {
        student_id: r.student_id,
        full_name: null,
        initials: "??",
        avatar_url: null,
        university: stu?.university ?? null,
        major: stu?.major ?? null,
        graduation_year: stu?.graduation_year ?? null,
        graduation_term: stu?.graduation_term ?? null,
        state: stu?.state ?? null,
        city: stu?.city ?? null,
        cases: [caseRef],
        shortlisted: false,
        submission_count: 0,
        last_active_at: null,
        most_recent_registered_at: r.created_at,
      });
    }
  }

  for (const p of (profiles ?? []) as { id: string; full_name: string | null; avatar_url: string | null }[]) {
    const row = byStudent.get(p.id);
    if (row) {
      row.full_name = p.full_name;
      row.initials = makeInitialsForAll(p.full_name);
      row.avatar_url = p.avatar_url ?? null;
    }
  }

  for (const s of (short ?? []) as { id: string; student_id: string; competition_id: string }[]) {
    const row = byStudent.get(s.student_id);
    if (!row) continue;
    row.shortlisted = true;
    const ref = row.cases.find((c) => c.competition_id === s.competition_id);
    if (ref) ref.shortlist_entry_id = s.id;
  }

  // Submissions: count per team and find max(submitted_at) per team. Then
  // attribute both to every student on that team.
  type MemberRow = { student_id: string; team_id: string; competition_id: string };
  const memberRows = (members ?? []) as MemberRow[];
  const teamIds = Array.from(new Set(memberRows.map((m) => m.team_id))).filter(Boolean);

  if (teamIds.length > 0) {
    const { data: subs } = await _supabase_for_all_candidates
      .from("submissions")
      .select("team_id, submitted_at")
      .in("team_id", teamIds);

    type SubRow = { team_id: string; submitted_at: string | null };
    const subCountByTeam = new Map<string, number>();
    const lastSubByTeam = new Map<string, string | null>();
    for (const s of (subs ?? []) as SubRow[]) {
      subCountByTeam.set(s.team_id, (subCountByTeam.get(s.team_id) ?? 0) + 1);
      const prev = lastSubByTeam.get(s.team_id) ?? null;
      if (s.submitted_at && (!prev || new Date(s.submitted_at).getTime() > new Date(prev).getTime())) {
        lastSubByTeam.set(s.team_id, s.submitted_at);
      }
    }

    for (const m of memberRows) {
      const row = byStudent.get(m.student_id);
      if (!row) continue;
      row.submission_count += subCountByTeam.get(m.team_id) ?? 0;
      const ts = lastSubByTeam.get(m.team_id) ?? null;
      if (ts && (!row.last_active_at || new Date(ts).getTime() > new Date(row.last_active_at).getTime())) {
        row.last_active_at = ts;
      }
    }
  }

  // Backfill last_active_at from registration when no submissions seen.
  for (const row of byStudent.values()) {
    if (!row.last_active_at) row.last_active_at = row.most_recent_registered_at;
  }

  // Sort by most recent registration (default ordering — the page can re-sort).
  return [...byStudent.values()].sort((a, b) => {
    const at = a.most_recent_registered_at ? new Date(a.most_recent_registered_at).getTime() : 0;
    const bt = b.most_recent_registered_at ? new Date(b.most_recent_registered_at).getTime() : 0;
    return bt - at;
  });
}
